<template>
    <div class="login-page">
        <h3 class="system-name">智慧云平台</h3>
        <div class="login-container">
            <div class="welcome-text">欢迎登录</div>
            <el-form class="login-form" ref="loginFormRef" :model="loginForm" :rules="loginRules" size="large">
                <el-form-item prop="username">
                    <el-input placeholder="请输入账号" v-model="loginForm.username" autocomplete="off">
                        <template #prefix>
                            <el-icon color="#303133" :size="20">
                                <User />
                            </el-icon>
                        </template>
                    </el-input>
                </el-form-item>
                <el-form-item prop="password">
                    <el-input placeholder="请输入密码" v-model="loginForm.password" autocomplete="off" show-password>
                        <template #prefix>
                            <el-icon color="#303133" :size="20">
                                <Lock />
                            </el-icon>
                        </template>
                    </el-input>
                </el-form-item>
                <el-form-item prop="code">
                    <el-input class="input-code" placeholder="请输入验证码" v-model="loginForm.code" @keyup.enter="handlerLogin">
                        <template #prefix>
                            <i class="iconfont icon-auth-code" :style="{ color: '#303133', fontSize: '20px' }"></i>
                        </template>
                    </el-input>
                    <div class="show-code">
                        <el-image
                            ref="imgCodeRef"
                            class="img-code"
                            :src="imgCaptchaCode"
                            fit="contain"
                            @click="reloadCaptchaCode"
                        ></el-image>
                    </div>
                </el-form-item>
                <!-- 登录按钮 -->
                <el-button class="login-btn" type="primary" :loading="loading" @click="handlerLogin">登录</el-button>
            </el-form>
        </div>
    </div>
</template>

<script setup>
// 导入的组件可以直接使用
import { onActivated, onMounted, ref } from 'vue'
import { validatePassword } from './rules'
import { useStore } from 'vuex'
import { getRsa } from '@/api/sys.js'
import JSEncrypt from 'jsencrypt'
import { Base64 } from 'js-base64'
// 数据源
const loginForm = ref({
    username: '',
    password: '',
    code: ''
})
const ApiCaptchaCode = '/api/captcha/code'
const imgCaptchaCode = ref('')

// 验证规则
const loginRules = ref({
    username: [
        {
            required: true,
            trigger: 'blur',
            message: '请输入用户名'
        }
    ],
    password: [
        {
            required: true,
            trigger: 'blur',
            validator: validatePassword()
        }
    ],
    code: [
        {
            required: true,
            message: '请输入验证码',
            trigger: 'blur'
        },
        {
            min: 4,
            max: 4,
            message: '长度为4个字符',
            trigger: 'blur'
        }
    ]
})

// 处理密码框文本显示
const passwordType = ref('password')
const onChangePwdType = () => {
    if (passwordType.value === 'password') {
        passwordType.value = 'text'
    } else {
        passwordType.value = 'password'
    }
}

// 处理登录
const loading = ref(false)
const store = useStore()
const loginFormRef = ref(null)
const handlerLogin = () => {
    // 1. 进行表单校验
    loginFormRef.value.validate((valid) => {
        if (!valid) return
        // 2. 触发登录动作
        loading.value = true
        const params = { ...loginForm.value }
        // 加密
        const encryptor = new JSEncrypt()
        encryptor.setPublicKey(rsaPubkey.value)
        const b64Passwod = Base64.encode(params.password) // 先base64加密
        params.password = encryptor.encrypt(b64Passwod) // 后ras加密
        store
            .dispatch('user/login', params)
            .then(() => {
                loading.value = false
            })
            .catch(() => {
                // console.log(err)
                loading.value = false
                reloadCaptchaCode()
                loginForm.value.code = ''
            })
    })
}

const imgCodeRef = ref()
// 刷新验证码
const reloadCaptchaCode = () => {
    imgCaptchaCode.value = ApiCaptchaCode + '?_=' + Math.random()
}

const rsaPubkey = ref('')

onActivated(() => {
    reloadCaptchaCode()
})
onMounted(() => {
    getRsa().then((data) => {
        rsaPubkey.value = Base64.decode(data.rsa_pubkey)
        reloadCaptchaCode()
    })
})
</script>

<style lang="scss" scoped>
$bg: #f1f3f6;
$white: #fff;
$black_gray: #333;
$dark_gray: #889aa4;
$light_gray: #eee;
$cursor: #fff;

.login-page {
    min-height: 100%;
    width: 100%;
    overflow: hidden;
    background: url('../../assets/images/login-bg.jpg') no-repeat center center / cover;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .system-name {
        width: 500px;
        padding: 20px 0;
        //border-radius: 15px;
        border-top-left-radius: 15px;
        border-top-right-radius: 15px;
        font-size: 36px;
        line-height: 62px;
        text-align: center;
        background-color: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(8px);
        letter-spacing: 30px;
        font-family: 'header-title', serif;
    }
    .login-container {
        width: 300px;
        padding: 30px 100px 60px;
        //border-radius: 15px;
        border-bottom-left-radius: 15px;
        border-bottom-right-radius: 15px;
        overflow: hidden;
        background-color: rgba(255, 255, 255, 0.3);
        backdrop-filter: blur(10px);
        //margin-top: 10px;
    }

    .welcome-text {
        font-size: 20px;
        color: #303133;
        text-align: center;
        letter-spacing: 5px;
    }

    .login-form {
        margin-top: 30px;

        :deep(.el-input__wrapper) {
            //background-color: transparent;
            background-color: rgba(255, 255, 255, 0.5);
            backdrop-filter: blur(8px);
        }

        :deep(.el-input__inner) {
            background-color: transparent !important;
            -webkit-box-shadow: 0 0 0 30px transparent inset !important;
            color: #303133;
        }

        :deep(input) {
            background-color: transparent;
        }

        :deep(input:-webkit-autofill),
        :deep(input:-webkit-autofill:hover),
        :deep(input:-webkit-autofill:focus),
        :deep(input:-webkit-autofill:active) {
            -webkit-transition-delay: 999999s;
            -webkit-transition: color 999999s ease-in, background-color 999999s ease-in;
        }
    }

    .show-code {
        position: absolute;
        top: 1px;
        right: 1px;

        .img-code {
            height: 38px;
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
        }
    }

    .login-btn {
        width: 100%;
        height: 45px;
    }
}
</style>
